import { AxiosRequestConfig, AxiosResponse } from "axios";
import CompetitorProductRequest from "../models/product/CompetitorProductRequest";
import { TesaPrototypePagedRes } from "../models/product/TesaPrototype";
import { getRowsPerPage } from "../../helper/UserSettingsHelper";
import Api from "./Api";
import PagedResponse from "../models/PagedResponse";
import ProductAttachment from "../models/product/ProductAttachment";
import { AttachmentType } from "../models/AttachmentType";
import { UUID } from "../models/Uuid";
import CompetitorProduct from "../models/product/CompetitorProduct";
import BaseProductResponse from "../models/product/BaseProductResponse";
import ProductNameResponse from "../models/product/ProductNameResponse";
import NartResponse from "../models/product/NartResponse";

export type CompetitorCompany = ProductRaw.Schemas.CompanyResponse;
export type TesaPrototypeRequest = ProductRaw.Schemas.TesaPrototypeRequest;
export type TesaPrototypeResponse = ProductRaw.Schemas.TesaPrototypeResponse;
export type UnknownProductRequest = ProductRaw.Schemas.UnknownProductRequest;
export type UnknownProductResponse = ProductRaw.Schemas.UnknownProductResponse;
export type ProductPropertiesResponse = TestRequestRaw.Schemas.ProductPropertiesResponse;

class ProductApi {
  constructor(private api: Api) {
    this.api = process.env.REACT_APP_PRODUCT_API_SERVER
      ? api.withBaseUrl(process.env.REACT_APP_PRODUCT_API_SERVER)
      : api;
  }

  public getBaseProducts(baseProduct: string): Promise<AxiosResponse<BaseProductResponse[]>> {
    const params = new URLSearchParams([["searchTerm", baseProduct]]);
    return this.api.get(`/test-request/v2/products/baseproduct`, { params });
  }

  public getProductNames(productName: string): Promise<AxiosResponse<ProductNameResponse[]>> {
    const params = new URLSearchParams([["searchTerm", productName]]);
    return this.api.get(`/test-request/v2/products/productnames`, { params });
  }

  public getNarts(nart: string): Promise<AxiosResponse<NartResponse[]>> {
    const params = new URLSearchParams([["searchTerm", nart]]);
    return this.api.get(`/test-request/v2/products/narts`, { params });
  }

  public getNartsByProductName(productName: string, searchTerm: string): Promise<AxiosResponse<NartResponse[]>> {
    const params = new URLSearchParams([["searchTerm", searchTerm]]);
    return this.api.get(`/test-request/v2/products/by-productname/${productName}/narts`, { params });
  }

  public getNartsByBaseProduct(baseProduct: string, searchTerm: string): Promise<AxiosResponse<NartResponse[]>> {
    const params = new URLSearchParams([["searchTerm", searchTerm]]);
    return this.api.get(`/test-request/v2/products/by-baseproduct/${baseProduct}/narts`, { params });
  }

  public getProductWithPropsByBaseProduct(baseProduct: string): Promise<AxiosResponse<ProductPropertiesResponse>> {
    return this.api.get(`/test-request/v2/products/by-baseproduct/${baseProduct}/properties`);
  }

  public getProductWithPropsByNart(nart: string): Promise<AxiosResponse<ProductPropertiesResponse>> {
    return this.api.get(`/test-request/v2/products/by-nart/${nart}/properties`);
  }

  public getProductWithPropsByProductName(productName: string): Promise<AxiosResponse<ProductPropertiesResponse>> {
    return this.api.get(`/test-request/v2/products/by-productname/${productName}/properties`);
  }

  public getCompetitorCompanies(
    query?: string,
    pageIdx?: number,
    pageSize?: number,
  ): Promise<AxiosResponse<PagedResponse<CompetitorCompany>>> {
    const queryParam = `&query=${query}`;
    return this.api.get(
      `/product/companies?pageIdx=${pageIdx || 0}&pageSize=${pageSize || query ? getRowsPerPage() : 0}${
        query ? queryParam : ""
      }`,
    );
  }

  public createNewCompany(payload: CompetitorCompany): Promise<void | AxiosResponse> {
    return this.api.post("/product/companies", payload);
  }

  public getCompetitorProduct(
    query?: string,
    companyId?: number | string,
    pageIdx?: number,
    pageSize?: number,
  ): Promise<AxiosResponse<PagedResponse<CompetitorProduct>>> {
    const queryUri = query ? `&query=${query}` : "";
    const companyIdUri = companyId ? `companyId=${companyId}&` : "";
    return this.api.get(
      `/product/competitor-products?${companyIdUri}pageIdx=${pageIdx || 0}&pageSize=${
        pageSize || getRowsPerPage()
      }${queryUri}`,
    );
  }

  public createNewCompetitorProduct(payload: CompetitorProductRequest): Promise<AxiosResponse> {
    return this.api.post("/product/competitor-products", payload);
  }

  public updateCompetitorProduct(
    productId: number,
    payload: CompetitorProductRequest,
  ): Promise<AxiosResponse<CompetitorProduct>> {
    return this.api.put(`/product/competitor-products/${productId}`, payload);
  }

  public isEditableCompetitorProduct(productId: number): Promise<AxiosResponse<boolean>> {
    return this.api.get(`/product/competitor-products/${productId}/updatable`);
  }

  public validateCompetitorProductName(companyId: number | null, productName: string): Promise<AxiosResponse> {
    return this.api.get(`/product/competitor-products/exists/${companyId}/${productName}`);
  }

  public addAttachmentCompetitorProduct(productId: number, type: AttachmentType, token: string) {
    const config: AxiosRequestConfig = {
      headers: {
        "content-type": "text/plain",
      },
    };
    return this.api
      .put(`/product/competitor-products/${productId}/attachments/${type}`, token, config)
      .then((res: AxiosResponse<CompetitorProduct>) => res.data.attachments);
  }

  public removeAttachmentCompetitorProduct(productId: number, attachmentReference: UUID) {
    const config = {
      headers: {
        "content-type": "application/json",
      },
    };
    return this.api
      .delete(`/product/competitor-products/${productId}/attachments`, JSON.stringify(attachmentReference), config)
      .then((res: AxiosResponse<CompetitorProduct>) => res.data.attachments);
  }

  public fetchClearanceTokenCompetitorProduct(productId: number): Promise<AxiosResponse<string>> {
    return this.api.get(`/product/competitor-products/${productId}/clearance-token`);
  }

  public getPrototypes(query?: string, pageIdx?: number, pageSize?: number) {
    const queryParam = `&query=${query}`;
    return this.api.get(
      `/product/tesa-prototypes?pageIdx=${pageIdx || 0}&pageSize=${pageSize || getRowsPerPage()}${
        query ? queryParam : ""
      }`,
    );
  }

  public createNewPrototype(payload: TesaPrototypeRequest): Promise<AxiosResponse<TesaPrototypeResponse>> {
    return this.api.post("/product/tesa-prototypes", payload);
  }

  public updatePrototype(
    productId: number,
    payload: TesaPrototypeRequest,
  ): Promise<AxiosResponse<TesaPrototypeResponse>> {
    return this.api.put(`/product/tesa-prototypes/${productId}`, payload);
  }

  public isEditablePrototype(productId: number): Promise<AxiosResponse<boolean>> {
    return this.api.get(`/product/tesa-prototypes/${productId}/updatable`);
  }

  public validatePrototypeName(laboratoryId: number, prototypeName: string): Promise<AxiosResponse> {
    return this.api.get(`/product/tesa-prototypes/exists/${laboratoryId}/${prototypeName}`);
  }

  public getLaboratories(
    query?: string,
    pageIdx?: number,
    pageSize?: number,
  ): Promise<AxiosResponse<TesaPrototypePagedRes>> {
    const queryParam = `&query=${query}`;
    return this.api.get(
      `/product/laboratories?pageIdx=${pageIdx || 0}&pageSize=${pageSize || query ? getRowsPerPage() : 0}${
        query ? queryParam : ""
      }`,
    );
  }

  public createNewLaboratory(name: { name: string }): Promise<AxiosResponse> {
    return this.api.post("/product/laboratories", name);
  }

  public getUnknownProducts(
    query?: string,
    pageIdx?: number,
    pageSize?: number,
  ): Promise<AxiosResponse<ProductRaw.Schemas.UnknownProductPagedResult>> {
    const queryParam = `&query=${query}`;
    return this.api.get(
      `/product/special-products?pageIdx=${pageIdx || 0}&pageSize=${pageSize || getRowsPerPage()}${
        query ? queryParam : ""
      }`,
    );
  }

  public getSingleUnknownProduct(id: number): Promise<AxiosResponse<UnknownProductResponse>> {
    return this.api.get(`/product/special-products/${id}`);
  }

  public createNewUnknownProduct(product: UnknownProductRequest): Promise<AxiosResponse<UnknownProductResponse>> {
    return this.api.post("/product/special-products", product);
  }

  public updateUnknownProduct(
    productId: number,
    payload: UnknownProductRequest,
  ): Promise<AxiosResponse<UnknownProductResponse>> {
    return this.api.put(`/product/unknown-products/${productId}`, payload);
  }

  public isEditableUnknownProduct(productId: number): Promise<AxiosResponse<boolean>> {
    return this.api.get(`/product/unknown-products/${productId}/updatable`);
  }

  public validateUnknownProductName(productName: string): Promise<AxiosResponse<boolean>> {
    return this.api.get(`/product/special-products/exists/${productName}`);
  }

  public static readAttachments(
    productSampleId: number,
    response: AxiosResponse<{
      productSampleReferences: {
        productSampleId: number;
        productSampleAttachments: ProductAttachment[];
      }[];
    }>,
  ): ProductAttachment[] {
    return response.data.productSampleReferences
      .filter((s) => s.productSampleId === productSampleId)
      .flatMap((s) => s.productSampleAttachments);
  }

  public removeAttachment(testRequestId: string, productSampleId: number, ids: string[]): Promise<ProductAttachment[]> {
    return this.api
      .delete(`/test-request/v2/testrequests/${testRequestId}/product-samples/${productSampleId}/attachments`, ids)
      .then((response) => ProductApi.readAttachments(productSampleId, response));
  }

  public addAttachment(testRequestId: string, productSampleId: number, ids: string[]): Promise<ProductAttachment[]> {
    return this.api
      .put(`/test-request/v2/testrequests/${testRequestId}/product-samples/${productSampleId}/attachments`, ids)
      .then((response) => ProductApi.readAttachments(productSampleId, response));
  }
}

export default ProductApi;
